import { BaseClient } from '../client/BaseClient';
import { ApiResponse } from '../types';

export interface Product {
  _id: string;
  tel: string;
  productId: string;
  title: string;
  description?: string;
  price?: string;
  currency?: string;
  imageUrl?: string;
  category?: string;
  visibility: 'visible' | 'hidden';
  inStock: boolean;
  inventory?: number;
  sku?: string;
  metadata?: Record<string, any>;
  createdAt: Date;
  updatedAt: Date;
}

export interface Order {
  _id: string;
  tel: string;
  orderId: string;
  customerId: string;
  status: 'pending' | 'confirmed' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
  items: Array<{
    productId: string;
    title: string;
    quantity: number;
    price: string;
    currency: string;
  }>;
  total: {
    amount: string;
    currency: string;
  };
  shipping?: {
    address: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
  payment?: {
    method: string;
    status: 'pending' | 'paid' | 'failed' | 'refunded';
    transactionId?: string;
  };
  notes?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface Cart {
  _id: string;
  tel: string;
  customerId: string;
  items: Array<{
    productId: string;
    title: string;
    quantity: number;
    price: string;
    currency: string;
  }>;
  total: {
    amount: string;
    currency: string;
  };
  expiresAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateProductRequest {
  tel: string;
  title: string;
  description?: string;
  price: string;
  currency: string;
  imageUrl?: string;
  category?: string;
  visibility?: 'visible' | 'hidden';
  inStock?: boolean;
  inventory?: number;
  sku?: string;
  metadata?: Record<string, any>;
}

export interface UpdateProductRequest {
  title?: string;
  description?: string;
  price?: string;
  currency?: string;
  imageUrl?: string;
  category?: string;
  visibility?: 'visible' | 'hidden';
  inStock?: boolean;
  inventory?: number;
  sku?: string;
  metadata?: Record<string, any>;
}

export interface ProductsResponse {
  success: boolean;
  data: {
    products: Product[];
    total: number;
    page: number;
    limit: number;
  };
}

export interface ProductResponse {
  success: boolean;
  data: Product;
}

export interface OrdersResponse {
  success: boolean;
  data: {
    orders: Order[];
    total: number;
    page: number;
    limit: number;
  };
}

export interface OrderResponse {
  success: boolean;
  data: Order;
}

export interface CartResponse {
  success: boolean;
  data: Cart;
}

export class Business {
  constructor(private client: BaseClient) {}

  // Product Management

  /**
   * List all products for a session
   * @param tel - Phone number
   * @param params - Query parameters for filtering and pagination
   */
  async listProducts(tel: string, params?: {
    category?: string;
    visibility?: 'visible' | 'hidden';
    inStock?: boolean;
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<ProductsResponse> {
    return this.client['get']<ProductsResponse>('/api/catalog/products', {
      params: { tel, ...params },
    });
  }

  /**
   * Get a specific product by ID
   * @param tel - Phone number
   * @param productId - Product ID
   */
  async getProduct(tel: string, productId: string): Promise<ProductResponse> {
    return this.client['get']<ProductResponse>(`/api/catalog/products/${productId}`, {
      params: { tel },
    });
  }

  /**
   * Create a new product
   * @param productData - Product creation data
   */
  async createProduct(productData: CreateProductRequest): Promise<ProductResponse> {
    return this.client['post']<ProductResponse>('/api/catalog/products', productData);
  }

  /**
   * Update an existing product
   * @param tel - Phone number
   * @param productId - Product ID
   * @param updates - Product update data
   */
  async updateProduct(tel: string, productId: string, updates: UpdateProductRequest): Promise<ProductResponse> {
    return this.client['put']<ProductResponse>(`/api/catalog/products/${productId}`, {
      tel,
      ...updates,
    });
  }

  /**
   * Delete a product
   * @param tel - Phone number
   * @param productId - Product ID
   */
  async deleteProduct(tel: string, productId: string): Promise<{ success: boolean }> {
    return this.client['delete']<{ success: boolean }>(`/api/catalog/products/${productId}`, {
      data: { tel },
    });
  }

  /**
   * Update product inventory
   * @param tel - Phone number
   * @param productId - Product ID
   * @param inventory - New inventory count
   * @param inStock - Stock status
   */
  async updateInventory(tel: string, productId: string, inventory: number, inStock?: boolean): Promise<ProductResponse> {
    return this.client['patch']<ProductResponse>(`/api/catalog/products/${productId}/inventory`, {
      tel,
      inventory,
      inStock: inStock ?? (inventory > 0),
    });
  }

  // Order Management

  /**
   * List all orders for a session
   * @param tel - Phone number
   * @param params - Query parameters for filtering and pagination
   */
  async listOrders(tel: string, params?: {
    status?: string;
    customerId?: string;
    fromDate?: string;
    toDate?: string;
    limit?: number;
    offset?: number;
  }): Promise<OrdersResponse> {
    return this.client['get']<OrdersResponse>('/api/orders', {
      params: { tel, ...params },
    });
  }

  /**
   * Get a specific order by ID
   * @param tel - Phone number
   * @param orderId - Order ID
   */
  async getOrder(tel: string, orderId: string): Promise<OrderResponse> {
    return this.client['get']<OrderResponse>(`/api/orders/${orderId}`, {
      params: { tel },
    });
  }

  /**
   * Confirm an order
   * @param tel - Phone number
   * @param orderId - Order ID
   * @param notes - Optional confirmation notes
   */
  async confirmOrder(tel: string, orderId: string, notes?: string): Promise<OrderResponse> {
    return this.client['post']<OrderResponse>(`/api/orders/${orderId}/confirm`, {
      tel,
      notes,
    });
  }

  /**
   * Update order status
   * @param tel - Phone number
   * @param orderId - Order ID
   * @param status - New order status
   * @param notes - Optional status update notes
   */
  async updateOrderStatus(
    tel: string,
    orderId: string,
    status: 'pending' | 'confirmed' | 'processing' | 'shipped' | 'delivered' | 'cancelled',
    notes?: string
  ): Promise<OrderResponse> {
    return this.client['patch']<OrderResponse>(`/api/orders/${orderId}/status`, {
      tel,
      status,
      notes,
    });
  }

  /**
   * Cancel an order
   * @param tel - Phone number
   * @param orderId - Order ID
   * @param reason - Cancellation reason
   */
  async cancelOrder(tel: string, orderId: string, reason?: string): Promise<OrderResponse> {
    return this.client['post']<OrderResponse>(`/api/orders/${orderId}/cancel`, {
      tel,
      reason,
    });
  }

  // Shopping Cart Management

  /**
   * Create a new shopping cart for a customer
   * @param tel - Phone number
   * @param customerId - Customer ID
   * @param expirationHours - Cart expiration in hours (default: 24)
   */
  async createCart(tel: string, customerId: string, expirationHours: number = 24): Promise<CartResponse> {
    return this.client['post']<CartResponse>('/api/carts', {
      tel,
      customerId,
      expirationHours,
    });
  }

  /**
   * Get a customer's cart
   * @param tel - Phone number
   * @param customerId - Customer ID
   */
  async getCart(tel: string, customerId: string): Promise<CartResponse> {
    return this.client['get']<CartResponse>(`/api/carts/${customerId}`, {
      params: { tel },
    });
  }

  /**
   * Add item to cart
   * @param tel - Phone number
   * @param customerId - Customer ID
   * @param productId - Product ID to add
   * @param quantity - Quantity to add
   */
  async addToCart(tel: string, customerId: string, productId: string, quantity: number = 1): Promise<CartResponse> {
    return this.client['post']<CartResponse>(`/api/carts/${customerId}/items`, {
      tel,
      productId,
      quantity,
    });
  }

  /**
   * Update item quantity in cart
   * @param tel - Phone number
   * @param customerId - Customer ID
   * @param productId - Product ID to update
   * @param quantity - New quantity
   */
  async updateCartItem(tel: string, customerId: string, productId: string, quantity: number): Promise<CartResponse> {
    return this.client['patch']<CartResponse>(`/api/carts/${customerId}/items/${productId}`, {
      tel,
      quantity,
    });
  }

  /**
   * Remove item from cart
   * @param tel - Phone number
   * @param customerId - Customer ID
   * @param productId - Product ID to remove
   */
  async removeFromCart(tel: string, customerId: string, productId: string): Promise<CartResponse> {
    return this.client['delete']<CartResponse>(`/api/carts/${customerId}/items/${productId}`, {
      data: { tel },
    });
  }

  /**
   * Clear entire cart
   * @param tel - Phone number
   * @param customerId - Customer ID
   */
  async clearCart(tel: string, customerId: string): Promise<{ success: boolean }> {
    return this.client['delete']<{ success: boolean }>(`/api/carts/${customerId}`, {
      data: { tel },
    });
  }

  /**
   * Convert cart to order
   * @param tel - Phone number
   * @param customerId - Customer ID
   * @param shipping - Shipping information
   * @param payment - Payment information
   */
  async checkoutCart(
    tel: string,
    customerId: string,
    shipping?: {
      address: string;
      city: string;
      state: string;
      postalCode: string;
      country: string;
    },
    payment?: {
      method: string;
    }
  ): Promise<OrderResponse> {
    return this.client['post']<OrderResponse>(`/api/carts/${customerId}/checkout`, {
      tel,
      shipping,
      payment,
    });
  }

  // Business Analytics

  /**
   * Get product analytics
   * @param tel - Phone number
   * @param period - Time period ('day', 'week', 'month')
   */
  async getProductAnalytics(tel: string, period: 'day' | 'week' | 'month' = 'week'): Promise<{
    success: boolean;
    data: {
      totalProducts: number;
      activeProducts: number;
      topSellingProducts: Array<{
        productId: string;
        title: string;
        sales: number;
        revenue: string;
      }>;
      categorySales: Record<string, number>;
      inventoryAlerts: Array<{
        productId: string;
        title: string;
        inventory: number;
      }>;
    };
  }> {
    return this.client['get']<any>('/api/business/analytics/products', {
      params: { tel, period },
    });
  }

  /**
   * Get order analytics
   * @param tel - Phone number
   * @param period - Time period ('day', 'week', 'month')
   */
  async getOrderAnalytics(tel: string, period: 'day' | 'week' | 'month' = 'week'): Promise<{
    success: boolean;
    data: {
      totalOrders: number;
      revenue: string;
      averageOrderValue: string;
      statusBreakdown: Record<string, number>;
      topCustomers: Array<{
        customerId: string;
        orders: number;
        revenue: string;
      }>;
    };
  }> {
    return this.client['get']<any>('/api/business/analytics/orders', {
      params: { tel, period },
    });
  }
}